import React, { useMemo } from 'react';
import { Result1 } from '../../types';
import { ResultCirclesSVG } from './ResultCirclesSVG';
import { StatusTooltip1 } from './StatusTooltip1';
import { isValidContinuousCode } from '@mm-shared/types/statusTypeGuards';
import { getBackgroundColorClass } from '../../utils/colorUtils';
import { ContinuousCode } from '@mm-shared/types/Status1';
import { getBackgroundColorForResultCircles, getTriangleIndicatorClass } from '../../utils/statusUtils';

// --- Presenter: Pure, stateless, receives all data and handlers as props ---
export interface StatusCellPresenterProps {
  objectiveIsParent: boolean;
  statusText: string;
  statusCode: string;
  hasResults: boolean;
  hasKeyResults: boolean;
  hasComments: boolean;
  resultsForInterval: Result1[];
  row: number;
  column: number;
  cellRef: React.RefObject<HTMLDivElement>;
  handleClick: (e: React.MouseEvent) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  handleFocus: (e: React.FocusEvent) => void;
  handleBlur: () => void;
  showStatusCodes: boolean;
  isOpen?: boolean;
  setIsOpen?: (open: boolean) => void;
  isInUse?: boolean;
  setIsInUse?: (inUse: boolean) => void;
  handleClose?: () => void;
  statusUuid: string;
  objectiveUuid: string;
  forIntervalEnding: string;
  milestoneMapUuid: string;
  activateTooltip?: boolean;
  focusPosition?: { row: number; col: number };
  hoverPosition?: { row: number; col: number };
  enableCrosshairs?: boolean;
  enableResultCircleFill?: boolean;
  showResultCircles?: boolean;
  // When true, apply row highlight styling (bg-blue-50) for drawer highlight feature
  isObjectiveHighlighted?: boolean;
  // When true, apply ring-2 ring-blue-500 to indicate this is the active status cell
  // (used by StatusCell3 when the drawer is open for this cell's objective+interval)
  isActiveStatusCell?: boolean;
  // Crosshairs highlight: when set, this is the interval ending of the highlighted column
  // (Status tab crosshairs effect)
  highlightedIntervalEnding?: string | null;
  // Crosshairs highlight: true when this cell's objective row is highlighted
  // (Status tab crosshairs effect — separate from isObjectiveHighlighted bg-blue-50 style)
  isHighlightedCrosshairRow?: boolean;
  toggleStatusCellDisplay?:
    | 'status'
    | 'status_results'
    | 'status_results_comments'
    | 'status_results2_comments';
  canEdit?: boolean;
  enableHumanHighlight?: boolean;
  isHighlightedCell?: boolean;
  /** UUID of the currently highlighted result — passed to ResultCirclesSVG to change circle stroke */
  highlightedResultUuid?: string | null;
}

export const StatusCellPresenter = React.memo(function StatusCellPresenter({
  objectiveIsParent,
  statusText,
  statusCode,
  hasResults,
  hasKeyResults,
  hasComments,
  resultsForInterval,
  row,
  column,
  cellRef,
  handleClick,
  handleKeyDown,
  handleFocus,
  handleBlur,
  showStatusCodes,
  isOpen,
  setIsOpen,
  isInUse,
  setIsInUse,
  handleClose,
  statusUuid,
  objectiveUuid,
  forIntervalEnding,
  milestoneMapUuid,
  activateTooltip = true,
  focusPosition = { row: -1, col: -1 },
  hoverPosition = { row: -1, col: -1 },
  enableCrosshairs = false,
  enableResultCircleFill = true,
  showResultCircles = true,
  isObjectiveHighlighted = false,
  isActiveStatusCell = false,
  toggleStatusCellDisplay = 'status_results2_comments',
  canEdit = true,
  enableHumanHighlight = false,
  isHighlightedCell = false,
  highlightedIntervalEnding = null,
  isHighlightedCrosshairRow = false,
  highlightedResultUuid = null,
}: StatusCellPresenterProps) {
  // Determine background color based on status code type
  // Use continuous colors (Tailwind classes) for metric objectives, discrete colors for deliverables
  // Parent objectives suppress background color to avoid visual confusion on child maps
  const backgroundColor = objectiveIsParent
    ? ''
    : isValidContinuousCode(statusCode)
      ? getBackgroundColorClass(statusCode as ContinuousCode) // Tailwind class for continuous codes (r1-r6, y7-y12, g13-g22)
      : getBackgroundColorForResultCircles(statusCode, hasResults, hasKeyResults); // Existing logic for discrete codes

  const cellClassNames = useMemo(() => {
    const classes = [
      'h-full',
      'w-full',
      'flex',
      'items-center',
      'gap-2',
      'px-2',
      'py-1',
      'min-h-8',
      'relative', // Added for absolute positioning of SVG
      backgroundColor,
    ];

    // Show triangle for comments based on toggle state
    const showTriangleIndicator =
      toggleStatusCellDisplay === 'status_results_comments' ||
      toggleStatusCellDisplay === 'status_results2_comments';
    if (hasComments && !objectiveIsParent && showTriangleIndicator) {
      classes.push(getTriangleIndicatorClass('topRight', 'gray-100'));
    }

    // Opacity priority: human highlight > drawer crosshairs > result highlight > Map3Control crosshairs
    if (enableHumanHighlight) {
      // Human highlight takes precedence over everything else
      classes.push(isHighlightedCell ? 'opacity-100' : 'opacity-10');
    } else if (highlightedIntervalEnding) {
      // Drawer crosshairs: Status tab column + row highlight effect
      // Active when highlightedIntervalEnding is set (Status tab highlight toggle active)
      // Takes precedence over Map3Control crosshairs but yields to human highlight.
      const columnMatches = forIntervalEnding === highlightedIntervalEnding;
      const rowMatches = isHighlightedCrosshairRow;
      classes.push(columnMatches || rowMatches ? 'opacity-100' : 'opacity-10');
    } else if (highlightedResultUuid) {
      // Result highlight: dim cells that don't contain the highlighted result
      const cellContainsHighlightedResult = resultsForInterval.some(
        (r) => r.uuid === highlightedResultUuid,
      );
      classes.push(cellContainsHighlightedResult ? 'opacity-100' : 'opacity-10');
    } else if (enableCrosshairs) {
      // Map3Control crosshairs (only when neither human nor drawer highlight is active)
      const isHighlighted = row === hoverPosition.row || column === hoverPosition.col;
      const tbodyHasFocus =
        (focusPosition.row !== -1 && focusPosition.col !== -1) ||
        (hoverPosition.row !== -1 && hoverPosition.col !== -1);
      classes.push(!tbodyHasFocus ? 'opacity-100' : isHighlighted ? 'opacity-100' : 'opacity-10');
      classes.push(
        statusText !== '' ? backgroundColor : isHighlighted ? 'bg-gray-200' : backgroundColor,
      );
    }

    // Objective highlight: apply bg-blue-50 when this cell's row is highlighted via drawer
    // Applied after crosshairs so it takes precedence for highlighted rows
    if (isObjectiveHighlighted) {
      classes.push('bg-blue-50');
    }

    // Active status cell: apply ring to indicate this is the cell whose drawer is open
    // Used by StatusCell3 to show focus ring on the clicked status cell
    if (isActiveStatusCell) {
      classes.push('ring-2', 'ring-blue-500', 'ring-inset');
    }

    return classes.join(' ');
  }, [
    backgroundColor,
    toggleStatusCellDisplay,
    hasComments,
    objectiveIsParent,
    enableHumanHighlight,
    isHighlightedCell,
    highlightedIntervalEnding,
    forIntervalEnding,
    isHighlightedCrosshairRow,
    highlightedResultUuid,
    resultsForInterval,
    enableCrosshairs,
    row,
    hoverPosition,
    column,
    focusPosition,
    statusText,
    isObjectiveHighlighted,
    isActiveStatusCell,
  ]);

  return (
    <>
      <div
        ref={cellRef}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={cellClassNames}
        data-status-cell="true"
        data-for-interval-ending={forIntervalEnding}
      >
        <div className="w-full text-center">{showStatusCodes ? statusText : ''}</div>

        {/* Result circles SVG */}
        {showResultCircles && !objectiveIsParent && resultsForInterval.length > 0 && (
          <ResultCirclesSVG
            statusCode={statusCode}
            results={resultsForInterval}
            className="absolute inset-0 pointer-events-none"
            enableFill={enableResultCircleFill}
            showStatusCodes={showStatusCodes}
            highlightedResultUuid={highlightedResultUuid}
          />
        )}
      </div>

      {activateTooltip && (
        <StatusTooltip1
          cellId={`col-${column}-row-${row}`}
          isOpen={isOpen!}
          setIsOpen={setIsOpen!}
          isInUse={isInUse!}
          setIsInUse={setIsInUse!}
          onClose={handleClose!}
          referenceElement={cellRef.current}
          statusUuid={statusUuid}
          objectiveUuid={objectiveUuid}
          forIntervalEnding={forIntervalEnding}
          milestoneMapUuid={milestoneMapUuid}
          canEdit={canEdit}
        />
      )}
    </>
  );
});
